import { Col } from 'antd'
import cx from 'classnames'
import _ from 'lodash'
import { PRODUCT_SHIP_TYPE } from '@vzmi/shp_shared_compo/constants/proposal'
import { Field, FormValueSelector } from '../../../../Common/forms'
import { InputCountField } from '../../../../Common/Form'
import SingleLineText from '../../../Common/ReadOnlyComponents/SingleLineText/SingleLineText'
import { ListingFieldConfig } from '../../../Common/ProposalForm/Shared/FieldConfig'
import { getIsTwoLayerListingFromSpreadModels } from '../../UpdateImage'
import styles from './index.css'

export const COUNT_LIMIT = {
  EXPRESS_24HR: 50,
  NON_EXPRESS_24HR: 100
}

export const productNameValidation = (selectFormValues, isTwoLayerListing) => (productName, formValues) => {
  const {
    sku,
    itemSku,
    isExpress24HR
  } = selectFormValues
  const productNameSize = _.size(productName)
  const countLimit = isExpress24HR ? COUNT_LIMIT.EXPRESS_24HR : COUNT_LIMIT.NON_EXPRESS_24HR
  if (productNameSize > countLimit) {
    if (!isExpress24HR) {
      return `最多${countLimit}個字`
    }

    return `進倉用標籤/品名需為${countLimit}個字內，請調整並確認內容是可供倉庫辨識的`
  }

  const spreadModels = _.get(formValues, 'spreadModels', [])
  const duplicatedProductNameMap = getDuplicatedProductNameMap(spreadModels, isTwoLayerListing)
  const isDuplicated = duplicatedProductNameMap[isTwoLayerListing ? itemSku : sku]
  if (isDuplicated) {
    return '商品名稱不可重複'
  }

  return null
}

export const ONE_LAYER_GROUPED_KEY = 'ONE_LAYER_GROUPED_KEY'
export const selectedValueValidation = (countLimit) => (selectedValue, formValues = {}) => {
  const { spreadModels } = formValues
  const isTwoLayerListing = getIsTwoLayerListingFromSpreadModels(spreadModels)
  if (isTwoLayerListing) {
    return null
  }

  const valueSize = _.size(selectedValue)
  if (valueSize === 0) {
    return '欄位不可為空'
  }

  if (valueSize > countLimit) {
    return `最多 ${countLimit} 個字`
  }

  const duplicatedMap = getDuplicatedSelectedValueMap(spreadModels)
  const isDuplicated = _.get(duplicatedMap, `${ONE_LAYER_GROUPED_KEY}.${selectedValue}`, false)
  if (isDuplicated) {
    return `${selectedValue} 不可重複`
  }

  return null
}

export const itemSelectedValueValidation = (selectFormValues, countLimit) => (itemSelectedValue, formValues = {}) => {
  const { spreadModels } = formValues
  const valueSize = _.size(itemSelectedValue)
  if (valueSize === 0) {
    return '欄位不可為空'
  }

  if (valueSize > countLimit) {
    return `最多 ${countLimit} 個字`
  }

  const { sku, selectedValue } = selectFormValues
  const duplicatedMapBySku = getDuplicatedSelectedValueMap(spreadModels)
  const isDuplicated = _.get(duplicatedMapBySku, `${sku}.${itemSelectedValue}`, false)

  if (isDuplicated) {
    return `${selectedValue}-${itemSelectedValue} 不可重複`
  }

  return null
}

export const getDuplicatedSelectedValueMap = (spreadModels) => {
  const isTwoLayerListing = getIsTwoLayerListingFromSpreadModels(spreadModels)
  const duplicatedSelectedValueMapBySku = _.flow(
    () => isTwoLayerListing
      // 雙層按照不同第一層屬性分區比對，所以用第一層的 sku 區分
      ? _.groupBy(spreadModels, 'sku')
      // 單層全部選擇的屬性名稱會一起比較所以分到同一群
      : { [ONE_LAYER_GROUPED_KEY]: spreadModels },
    groupedSpreadModels => _.reduce(groupedSpreadModels, (map, spreadModels, sku) => {
      const duplicatedMap = _.flow(
        () => {
          const models = isTwoLayerListing
            ? _.concat(
              _.map(spreadModels, 'items[0]')
            )
            : spreadModels
          return models
        },
        models => _.groupBy(_.map(models, 'spec.selectedValue')),
        groupedSelectedValues => _.reduce(groupedSelectedValues, (map, collect, selectedValue) => {
          map[selectedValue] = _.size(collect) > 1
          return map
        }, {})
      )()
      map[sku] = duplicatedMap
      return map
    }, {})
  )()
  return duplicatedSelectedValueMapBySku
}

export const getDuplicatedProductNameMap = spreadModels => {
  const isTwoLayerListing = getIsTwoLayerListingFromSpreadModels(spreadModels)
  const groupedProductNameMap = _.groupBy(spreadModels, 'productName')
  const duplicatedProductNameMap = _.reduce(spreadModels, (map, model = {}) => {
    const { sku, productName, items } = model
    const currentSku = isTwoLayerListing
      ? _.get(items, '0.sku')
      : sku
    map[currentSku] = (_.size(_.get(groupedProductNameMap, productName, [])) > 1)
    return map
  }, {})
  return duplicatedProductNameMap
}

const CustomImageCols = props => {
  const {
    readOnly,
    currentModelField,
    itemSpecHeader,
    onFieldChange = () => {},
    layerOneCountLimitMap = {},
    layerTwoCountLimitMap = {},
    isNoModelsListing
  } = props
  const Component = readOnly ? SingleLineText : InputCountField
  const isTwoLayerListing = !_.isEmpty(itemSpecHeader)
  const skuField = `${currentModelField}.sku`
  const itemSkuField = `${currentModelField}.items[0].sku`
  const selectedValueField = `${currentModelField}.spec.selectedValue`
  const itemSelectedValueField = `${currentModelField}.items[0].spec.selectedValue`

  return (
    <>
      {!isNoModelsListing && (
        <Col
          span={6}
          key='specValueCustomCol'
        >
          <FormValueSelector
            selector={select => ({
              sku: select(skuField),
              itemSku: select(itemSkuField),
              selectedValue: select(selectedValueField),
              itemSelectedValue: select(itemSelectedValueField)
            })}
          >
            {selectFormValues => {
              const { sku, itemSku } = selectFormValues
              const countLimit = layerOneCountLimitMap[sku]
              const itemCountLimit = layerTwoCountLimitMap[itemSku]
              return (
                <>
                  <div
                    className={cx(
                      styles.customCol,
                      styles.selectedValues
                    )}
                  >
                    <Field
                      name={selectedValueField}
                      countLimit={countLimit}
                      component={
                        (readOnly || !isTwoLayerListing)
                          ? Component
                          : InputCountField
                      }
                      onChange={onFieldChange}
                      validate={selectedValueValidation(countLimit)}
                      disabled={isTwoLayerListing}
                      fullWidth
                      showErrorOnActive
                      mustShowFormError
                      absoluteError
                    />
                  </div>
                  {isTwoLayerListing && (
                    <div
                      className={cx(
                        styles.customCol,
                        styles.selectedValues
                      )}
                    >
                      <Field
                        name={itemSelectedValueField}
                        countLimit={itemCountLimit}
                        component={Component}
                        onChange={onFieldChange}
                        validate={itemSelectedValueValidation(selectFormValues, itemCountLimit)}
                        fullWidth
                        showErrorOnActive
                        mustShowFormError
                        absoluteError
                      />
                    </div>
                  )}
                </>
              )
            }}
          </FormValueSelector>
        </Col>
      )}
      <Col
        span={isNoModelsListing ? 16 : 10}
        key='productNameAndPartNoCustomCol'
      >
        <FormValueSelector
          selector={select => ({
            sku: select(skuField),
            itemSku: select(itemSkuField),
            isExpress24HR: (select('skuDetail.shipType.id') === PRODUCT_SHIP_TYPE.EXPRESS_24HR)
          })}
        >
          {selectFormValues => {
            const { isExpress24HR } = selectFormValues
            return (
              <>
                <div className={styles.customCol}>
                  <Field
                    name={`${currentModelField}.productName`}
                    countLimit={
                      isExpress24HR
                        ? COUNT_LIMIT.EXPRESS_24HR
                        : COUNT_LIMIT.NON_EXPRESS_24HR
                    }
                    component={Component}
                    validate={productNameValidation(selectFormValues, isTwoLayerListing)}
                    fullWidth
                    showErrorOnActive
                    absoluteError
                  />
                </div>
                <div className={styles.customCol}>
                  {
                    !isNoModelsListing && (
                      <Field
                        {...ListingFieldConfig.getField('model.partNo')}
                        className={cx({ [styles.partNo]: readOnly })}
                        name={`${currentModelField}.${
                          isTwoLayerListing
                            // 雙層屬性 partNo 放在 items 底下
                            ? 'items[0].partNo'
                            // 單層屬性 partNo 放在 models 底下
                            : 'partNo'
                        }`}
                        component={Component}
                        fullWidth
                        showErrorOnActive
                        mustShowFormError
                        absoluteError
                      />
                    )
                  }
                </div>
              </>
            )
          }}
        </FormValueSelector>
      </Col>
    </>
  )
}

export default CustomImageCols
